import type { Editor, NodeEntry } from 'slate';

import { assertNeverThrow } from '../../util';
import type {
  AlineContentBlock,
  AlineElement,
  DynamicBlockElement,
  ParagraphElement,
  RenderModelContentBlock,
  RenderModelDynamicBlock,
  RenderModelElement,
  RenderModelFooter,
  RenderModelHeader,
  RenderModelParagraph,
  RenderModelTable,
  RenderModelTOC,
  TableElement,
  TOCElement,
} from '../internal';
import {
  buildRenderModelDynamicBlock,
  buildRenderModelParagraph,
  buildRenderModelTable,
  buildRenderModelTOC,
  FooterType,
  HeaderType,
  processInstructionFields,
  Queries,
  validateRMNodeCache,
} from '../internal';

/**
 * Caching system for render model nodes.
 *
 * The caching strategy uses:
 * - Cached nodes to optimize the render model building process.
 * - Cached headers and footers for each page so we don't
 *   need to recalculate them when main page content changes.
 * - WeakMap (key:original Slate Node, value: RenderModelNode | RenderModelNode[])
 *   to allow garbage collection when nodes are removed.
 *
 * Special handling for nodes containing page numbers:
 * - nodes in headers/footers with page numbers need different render models per page
 *   (stored as arrays indexed by page number in the cache)
 * - nodes in the main body with page numbers are tracked for cache refresh
 *   when total page count changes
 */

export type CacheableCMNode = ParagraphElement | TableElement | DynamicBlockElement | TOCElement;

export type CacheableRMNode = Extract<RenderModelElement, { type: CacheableCMNode['type'] }>;

/**
 * A cached render model node, including metadata that must match for there to be a cache hit.
 */
export interface CachedRMNodeInfo<T extends CacheableRMNode = CacheableRMNode> {
  cachedNode: T;
  containerWidthPx: number;
}

export type CacheableRMNodeFor<T extends CacheableCMNode> = T extends TOCElement
  ? RenderModelTOC
  : Extract<CacheableRMNode, { type: T['type'] }>;

export type CachedRMNodeInfoFor<T extends CacheableCMNode> = CachedRMNodeInfo<
  CacheableRMNodeFor<T>
>;

interface RenderModelCacheWeakMap extends WeakMap<
  AlineElement,
  CachedRMNodeInfo | CachedRMNodeInfo[]
> {
  get<T extends CacheableCMNode>(
    key: T
  ): CachedRMNodeInfoFor<T> | CachedRMNodeInfoFor<T>[] | undefined;
  set<T extends CacheableCMNode>(
    key: T,
    value: CachedRMNodeInfoFor<T> | CachedRMNodeInfoFor<T>[]
  ): this;
}

// Elements cache for all editor nodes
export const renderModelCache = new WeakMap() as RenderModelCacheWeakMap;

// Arrays of nodes that need to be refreshed in order to display correct total pages count
export const nodesThatNeedCacheRefresh = new Set<CacheableCMNode>();

// Cache to track if an element contains page numbers
export const containsPageNumbersCache = new WeakMap<AlineElement, boolean>();

// Page content height cache for each page
export const pageContentHeightCache = new Map<number, number>();

// Headers cache for each page
export const renderModelDefaultHeaderCache: Map<string, RenderModelHeader> = new Map();
export const renderModelFirstPageHeaderCache: Map<string, RenderModelHeader> = new Map();
export const renderModelEvenHeaderCache: Map<string, RenderModelHeader> = new Map();

// Footers cache for each page
export const renderModelDefaultFooterCache: Map<string, RenderModelFooter> = new Map();
export const renderModelFirstPageFooterCache: Map<string, RenderModelFooter> = new Map();
export const renderModelEvenFooterCache: Map<string, RenderModelFooter> = new Map();

export function getCachedRMNodeInfo<T extends CacheableCMNode>({
  cMNode,
  pageNum,
}: {
  cMNode: T;
  pageNum: number;
}): CachedRMNodeInfoFor<T> | null {
  const cachedValue = renderModelCache.get(cMNode);
  if (!cachedValue) return null;
  return Array.isArray(cachedValue) ? (cachedValue[pageNum - 1] ?? null) : cachedValue;
}

export function addRMNodeToCache<T extends CacheableCMNode>({
  cMNode,
  containerWidthPx,
  isUniqueInHeaderFooter,
  pageNum,
  rMNode,
}: {
  cMNode: T;
  containerWidthPx: number;
  isUniqueInHeaderFooter: boolean;
  pageNum: number;
  rMNode: CacheableRMNodeFor<T>;
}): void {
  const cachedRMNode: CachedRMNodeInfoFor<T> = {
    cachedNode: rMNode,
    containerWidthPx,
  };

  if (isUniqueInHeaderFooter) {
    const cachedRMElement = getOrCreateDynamicRMElementArray(cMNode);
    cachedRMElement[pageNum - 1] = cachedRMNode;
    return;
  }

  renderModelCache.set(cMNode, cachedRMNode);
}

function getOrCreateDynamicRMElementArray<T extends CacheableCMNode>(
  cMNode: T
): CachedRMNodeInfoFor<T>[] {
  let cachedArray = renderModelCache.get(cMNode);
  if (!cachedArray || !Array.isArray(cachedArray)) {
    cachedArray = [];
    renderModelCache.set(cMNode, cachedArray);
  }
  return cachedArray;
}

export function getCachedHeader(key: string, type: HeaderType): RenderModelHeader | undefined {
  switch (type) {
    case HeaderType.Default:
      return renderModelDefaultHeaderCache.get(key);
    case HeaderType.FirstPage:
      return renderModelFirstPageHeaderCache.get(key);
    case HeaderType.Even:
      return renderModelEvenHeaderCache.get(key);
  }
}

export function getCachedFooter(key: string, type: FooterType): RenderModelFooter | undefined {
  switch (type) {
    case FooterType.Default:
      return renderModelDefaultFooterCache.get(key);
    case FooterType.FirstPage:
      return renderModelFirstPageFooterCache.get(key);
    case FooterType.Even:
      return renderModelEvenFooterCache.get(key);
  }
}

export function setCachedHeader(key: string, type: HeaderType, value: RenderModelHeader): void {
  switch (type) {
    case HeaderType.Default:
      renderModelDefaultHeaderCache.set(key, value);
      break;
    case HeaderType.FirstPage:
      renderModelFirstPageHeaderCache.set(key, value);
      break;
    case HeaderType.Even:
      renderModelEvenHeaderCache.set(key, value);
      break;
  }
}

export function setCachedFooter(key: string, type: FooterType, value: RenderModelFooter): void {
  switch (type) {
    case FooterType.Default:
      renderModelDefaultFooterCache.set(key, value);
      break;
    case FooterType.FirstPage:
      renderModelFirstPageFooterCache.set(key, value);
      break;
    case FooterType.Even:
      renderModelEvenFooterCache.set(key, value);
      break;
  }
}

export function getHeaderCacheKey(headerType: HeaderType, pageNum: number): string {
  return `${headerType}-${pageNum}`;
}

export function getFooterCacheKey(footerType: FooterType, pageNum: number): string {
  return `${footerType}-${pageNum}`;
}

export function clearDefaultHeaderCache(): void {
  renderModelDefaultHeaderCache.clear();
}
export function clearFirstPageHeaderCache(): void {
  renderModelFirstPageHeaderCache.clear();
}
export function clearEvenHeaderCache(): void {
  renderModelEvenHeaderCache.clear();
}
export function clearDefaultFooterCache(): void {
  renderModelDefaultFooterCache.clear();
}
export function clearFirstPageFooterCache(): void {
  renderModelFirstPageFooterCache.clear();
}
export function clearEvenFooterCache(): void {
  renderModelEvenFooterCache.clear();
}

export function clearAllHeaderFooterCaches(): void {
  clearDefaultHeaderCache();
  clearFirstPageHeaderCache();
  clearEvenHeaderCache();
  clearDefaultFooterCache();
  clearFirstPageFooterCache();
  clearEvenFooterCache();
}

export function getOrCreateCachedRMBlockChild(
  editor: Editor,
  options: {
    containerWidthPx: number;
    isInHeaderFooter: boolean;
    nodeEntry: NodeEntry<AlineContentBlock>;
    pageNum: number;
  }
): RenderModelContentBlock {
  const { containerWidthPx, isInHeaderFooter, nodeEntry, pageNum } = options;
  const [node, path] = nodeEntry;

  const cachedNodeInfo = getCachedRMNodeInfo({
    cMNode: node,
    pageNum,
  });

  if (cachedNodeInfo) {
    const containsPageNumbersCached = containsPageNumbersCache.get(node) ?? false;
    const isUniqueInHeaderFooter = isInHeaderFooter && containsPageNumbersCached;

    const validatedCachedRMNode = validateRMNodeCache({
      cMNodeEntry: [node, path],
      cachedNodeInfo,
      containerWidthPx,
      isUniqueInHeaderFooter,
      pageNum,
    });

    if (validatedCachedRMNode) return validatedCachedRMNode;
  }

  const rMNode = getOrCreateCachedRMNode(editor, {
    cMNodeEntry: [node, path],
    containerWidthPx,
    isInHeaderFooter,
    pageNum,
  });

  return rMNode;
}

export function getOrCreateCachedRMNode<T extends CacheableCMNode>(
  editor: Editor,
  options: {
    cMNodeEntry: NodeEntry<T>;
    containerWidthPx: number;
    isInHeaderFooter: boolean;
    pageNum: number;
  }
): CacheableRMNodeFor<T> {
  const { cMNodeEntry, containerWidthPx, isInHeaderFooter, pageNum } = options;
  const [cMNode, cMNodePath] = cMNodeEntry;

  const cachedNodeInfo = getCachedRMNodeInfo({
    cMNode,
    pageNum,
  });

  if (cachedNodeInfo) {
    const containsPageNumbersCached = containsPageNumbersCache.get(cMNode) ?? false;
    const isUniqueInHeaderFooter = isInHeaderFooter && containsPageNumbersCached;

    const validatedCachedRMNode = validateRMNodeCache({
      cMNodeEntry: cMNodeEntry,
      cachedNodeInfo,
      containerWidthPx,
      isUniqueInHeaderFooter,
      pageNum,
    });

    if (validatedCachedRMNode) return validatedCachedRMNode;
  }

  const containsPageNumbers = Queries.containsPageNumbers(editor, cMNodePath);
  const isUniqueInHeaderFooter = containsPageNumbers && isInHeaderFooter;

  const rMNode = buildRenderModelNode(editor, {
    cMNodeEntry,
    containerWidthPx,
    containsPageNumbers,
    isInHeaderFooter,
    pageNum,
  });

  addRMNodeToCache({
    cMNode,
    containerWidthPx,
    isUniqueInHeaderFooter,
    pageNum,
    rMNode,
  });

  if (containsPageNumbers) {
    nodesThatNeedCacheRefresh.add(cMNode);
  }
  containsPageNumbersCache.set(cMNode, containsPageNumbers);

  return rMNode;
}

function buildRenderModelNode<T extends CacheableCMNode>(
  editor: Editor,
  options: {
    cMNodeEntry: NodeEntry<T>;
    containerWidthPx: number;
    containsPageNumbers: boolean;
    isInHeaderFooter: boolean;
    pageNum: number;
  }
): CacheableRMNodeFor<T> {
  const { cMNodeEntry, containerWidthPx, containsPageNumbers, isInHeaderFooter, pageNum } = options;
  const [cMNode, cMNodePath] = cMNodeEntry;

  /**
   * TypeScript isn't clever enough to know that if a cMNode has type 'paragraph', for instance, the
   * return type should be RenderModelParagraph. Type assertions are required to bridge this gap,
   * but are low-risk since we confirm that the actual type of the rMNode is what we expect it to be
   * before casting it.
   */
  switch (cMNode.type) {
    case 'paragraph': {
      const paragraphEntry: NodeEntry<ParagraphElement> = [
        containsPageNumbers ? processInstructionFields(editor, cMNode, pageNum) : cMNode,
        cMNodePath,
      ];

      const rMNode: RenderModelParagraph = buildRenderModelParagraph(editor, paragraphEntry, {
        containerWidthPx,
      });
      return rMNode as CacheableRMNodeFor<T>;
    }
    case 'table': {
      const rMNode: RenderModelTable = buildRenderModelTable(editor, {
        containerWidthPx,
        isInHeaderFooter,
        pageNum,
        tableEntry: [cMNode, cMNodePath],
      });
      return rMNode as CacheableRMNodeFor<T>;
    }
    case 'dynamic-block': {
      const rMNode: RenderModelDynamicBlock = buildRenderModelDynamicBlock(editor, {
        containerWidthPx,
        dynamicBlockEntry: [cMNode, cMNodePath],
        isInHeaderFooter,
        pageNum,
      });
      return rMNode as CacheableRMNodeFor<T>;
    }
    case 'instructionField': {
      const rMNode: RenderModelTOC = buildRenderModelTOC(editor, {
        containerWidthPx,
        isInHeaderFooter,
        pageNum,
        tocEntry: [cMNode, cMNodePath],
      });
      return rMNode as CacheableRMNodeFor<T>;
    }
    default: {
      assertNeverThrow(
        cMNode,
        'Unsupported content model node type encountered in buildRenderModelNode.'
      );
    }
  }
}
